import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { AutopilotCard } from '../Common/autopilot-card';
import { AHUControlProvider } from '@/contexts/ahu-control-context';
import { PowerControl } from './control/power-control';
import { OperationControl } from './control/operation-control';
import { AHUImage } from './control/ahu-image';
import { TemperatureControl } from './control/temperature-control';
import { ModeControl } from './control/mode-control';
import { FanControl } from './control/fan-control';
import { StatusPanel } from './control/status-panel';
import { AutoPilotStatus } from '@/types';
import { manualControlDevice } from '@/api/devices';
import { SUCCESS_MESSAGE } from '@/utils/constants';
import { App } from 'antd';
import { useControlEnumValues } from '@/utils/controlUtils';

interface AHUControlModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: {
    id: string;
    name: string
    autopilotId: string
    status: 'on' | 'off' | 'alarm' | undefined;
    setTemp: number | undefined;
    returnTemp: number | undefined;
    mode: 'cool' | 'fan' | 'dry' | null;
    opMode: string | undefined;
    fanSpeed: 'auto' | 'low' | 'med' | 'high' | undefined;
    autopilotStatus: AutoPilotStatus
    user: string;
    lastUpdated: string;
    alarmCode: string;
    isAlarm: boolean;
    isOnline: boolean;
  };
}

export function AHUControlModal({ isOpen, onClose, data }: AHUControlModalProps) {
  const [renderData, setRenderData] = useState(data)
  const { message } = App.useApp();
  const modalRef = useRef<HTMLDivElement>(null);
  const controlEnums = useControlEnumValues();

  const handlePowerChange = async (e: boolean) => {
    try {
      await manualControlDevice({
        device_id: data?.id,
        datapoint: 'status_write',
        value: controlEnums.getStatusValue(e ? 'on' : 'off')
      });
      message.open({
        type: 'success',
        content: SUCCESS_MESSAGE()
      });
    } catch (e) {
      message.open({
        type: 'error',
        content: (e as Error).message
      });
    }
  };

  // Operation is always Remote and disabled, so this handler doesn't need to do anything
  const handleOperationChange = async (_: boolean) => {
    // No-op function - we don't want to send any commands for operation mode
    console.log('Operation mode change ignored - always using Remote');
  };

  const handleTemperatureChange = async (e: number) => {
    try {
      await manualControlDevice({
        device_id: data?.id,
        datapoint: 'temperature_setpoint_write',
        value: e
      });
      message.open({
        type: 'success',
        content: SUCCESS_MESSAGE()
      });
    } catch (e) {
      message.open({
        type: 'error',
        content: (e as Error).message
      });
    }
  };

  const handleModeChange = async (e: string) => {
    try {
      await manualControlDevice({
        device_id: data?.id,
        datapoint: 'mode_write',
        value: controlEnums.getModeValue(e)
      });
      message.open({
        type: 'success',
        content: SUCCESS_MESSAGE()
      });
    } catch (e) {
      message.open({
        type: 'error',
        content: (e as Error).message
      });
    }
  };

  const handleFanChange = async (e: string) => {
    try {
      await manualControlDevice({
        device_id: data?.id,
        datapoint: 'fan_mode_write',
        value: controlEnums.getFanSpeedValue(e)
      });
      message.open({
        type: 'success',
        content: SUCCESS_MESSAGE()
      });
    } catch (e) {
      message.open({
        type: 'error',
        content: (e as Error).message
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose(); // Close the modal if clicked outside
      }
    };
    if (isOpen) {
      // Add the event listener when the modal is open
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      // Cleanup event listener when modal is closed
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    setRenderData(data)
  }, [data])

  if (!isOpen) return null;

  return (
    <AHUControlProvider initialState={renderData}>
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center !mt-0">
        <div ref={modalRef} className="w-[900px] bg-bg rounded-xl backdrop-blur-[20px] shadow-popup">
          {/* Header */}
          <div className="px-4 pt-4 pb-3 flex justify-between items-center border-light-grey">
            <h2 className="text-dark-blue heading-4">{renderData?.name}</h2>
            <button
              onClick={onClose}
              className="text-primary hover:text-dark-blue transition-colors"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex pt-2">
            {/* Left Column */}
            <div className="w-96 pb-4 pl-4 flex flex-col gap-2.5">
              <AutopilotCard
                id={renderData?.autopilotId}
                user={renderData?.user}
                lastUpdated={renderData?.lastUpdated}
                status={renderData?.autopilotStatus}
              />
              <AHUImage />
              {/* <div className="paragraph-10 text-dark-grey">Source: AC_remote</div> */}
            </div>

            {/* Right Column */}
            <div className="flex-1 px-4 pb-4 space-y-3">
              {/* Control Panel */}
              <div className="p-2.5 flex flex-col gap-2 bg-white rounded-[10px] border border-light-grey shadow-card">
                <h3 className="text-dark-blue heading-7">Control</h3>

                {/* Power & Operation */}
                <div className="grid grid-cols-2 gap-2">
                  <PowerControl onChange={handlePowerChange} />
                  <OperationControl onChange={handleOperationChange} />
                </div>

                {/* Temperature Controls */}
                <div>
                  <TemperatureControl
                    lastUpdated={renderData.lastUpdated}
                    returnTemp={renderData.returnTemp}
                    onChange={handleTemperatureChange}
                  />
                </div>

                {/* Mode Selection */}
                <div className="grid grid-cols-2 gap-2">
                  <ModeControl
                    ahuMode={renderData?.mode}
                    onChange={handleModeChange}
                  />
                  <FanControl
                    ahuFan={renderData?.fanSpeed || null}
                    onChange={handleFanChange}
                  />
                </div>
              </div>

              {/* Status Panel */}
              <StatusPanel
                isAlarm={renderData?.isAlarm}
                alarmCode={renderData?.alarmCode}
                connectionStatus={renderData?.isOnline ? 'online' : 'offline'}
              />
            </div>
          </div>
        </div>
      </div>
    </AHUControlProvider>
  );
}